<template>
  <Grid gtc="200px 1fr" class="request-price-place-orders overflow-hidden relative-position">
    <template v-if="quickViewItems">
      <ModalSidebar ref="modalSidebar" :items="quickViewItems" @item-selected="openQuickView" />

      <Grid gtr="1fr auto" class="overflow-hidden">
        <ModalBody>
          <Grid v-if="quickView" :key="quickView.id" gtc="1.5fr 1fr" gap="var(--r-space-sp)" align-items="start">
            <div
              v-if="isPlaceOrders"
              class="q-px-lg q-pt-lg bg-white-1 full-height shadow3 border-radius-sm overflow-hidden"
            >
              <EmailTemplateEditor :quickview-data="quickView" />
            </div>

            <div v-else class="q-pa-lg bg-white-1 full-height shadow3 border-radius-sm overflow-hidden">
              <EmailTemplateEditor :quickview-data="quickView" />
              <SubscriptionsTable
                :key="reRenderSubscriptionsTable"
                ref="subscriptionsTable"
                :data="quickView.subscription_periods"
              />
            </div>

            <Grid gap="var(--r-space-md)">
              <ContactsTable ref="contactsTable" :data="quickView" />

              <div class="attachments q-pa-lg bg-white-1 shadow3 border-radius-sm">
                <div class="text-subtitle-1 text-secondary-2">Attachments</div>

                <EmailAttachments
                  ref="emailAttachments"
                  :with-default-attachment="true"
                  @attachments-updated="attachments = $event"
                  @download-default-attachment="previewDefaultAttachment($event)"
                />
              </div>
            </Grid>

            <div v-if="isPlaceOrders" class="q-pa-lg bg-white-1 shadow3 border-radius-sm" style="grid-column: 1 / -1">
              <SubscriptionsTable
                :key="reRenderSubscriptionsTable"
                ref="subscriptionsTable"
                :data="quickView.subscription_periods"
                is-place-orders
                @start-loader="$emit('set-loader', true)"
                @re-fetch="reFetchQuickView"
              />
            </div>
          </Grid>
        </ModalBody>

        <ModalFooter>
          <RBtn size="big" type="ghost" @click="dismiss">Dismiss</RBtn>
          <RBtn size="big" type="primary" @click="send">Send</RBtn>
        </ModalFooter>
      </Grid>
    </template>
  </Grid>
</template>

<script>
import * as apiTo from '@/modular-api'
import { createFormData } from '@/utils/create-form-data'
import { checkQuickView, getTaskData } from '../modals-shared'
// Components
import EmailAttachments from '@components/lm-blocks/EmailAttachments'
import EmailTemplateEditor from '../components/EmailTemplateEditor'
import ModalSidebar from '../components/ModalSidebar'
import ModalBody from '../components/ModalBody'
import ModalFooter from '../components/ModalFooter'
import SubscriptionsTable from '../components/subscriptions-table/SubscriptionsTable'
import ContactsTable from '../components/contacts-table/ContactsTable'

export default {
  components: {
    EmailTemplateEditor,
    EmailAttachments,
    ModalSidebar,
    ModalBody,
    ModalFooter,
    SubscriptionsTable,
    ContactsTable
  },

  props: {
    isPlaceOrders: Boolean,
    periodId: [Number, String]
  },

  data() {
    return {
      reRenderSubscriptionsTable: 1,
      attachments: [],
      quickViewItems: null,
      quickView: null
    }
  },

  computed: {
    taskName() {
      if (this.isPlaceOrders) return 'place-orders'
      return 'request-prices'
    }
  },

  watch: {
    quickViewItems(newVal) {
      this.$emit('subtitle', newVal)
    }
  },

  created() {
    this.getTaskData(this.taskName).then(({ data }) => (this.quickViewItems = data.result))
  },

  methods: {
    getTaskData,
    checkQuickView,

    getCount() {
      return this.quickViewItems.reduce((acc, curr) => {
        acc += curr.subscriptions_count
        return acc
      }, 0)
    },

    openQuickView(quickViewItem) {
      this.$emit('set-loader', true)

      apiTo
        .GET_TASK_QUICK_VIEW(this.taskName, quickViewItem.id)
        .then(({ data }) => {
          this.quickView = {
            ...data.result,
            id: quickViewItem.id,
            email_template: data.result.email_templates.find(t => !!t.default)
          }
        })
        .finally(() => this.$emit('set-loader', false))
    },

    reFetchQuickView() {
      apiTo
        .GET_TASK_QUICK_VIEW(this.taskName, this.quickView.id)
        .then(({ data }) => {
          this.quickView.subscription_periods = data.result.subscription_periods
          this.reRenderSubscriptionsTable++

          // Update`subscription_count` in quickViewItems
          const quickViewItemIndex = this.quickViewItems.findIndex(q => q.id === this.quickView.id)
          if (~quickViewItemIndex) {
            this.quickViewItems[quickViewItemIndex].subscriptions_count = data.result.subscription_periods.length
          }
        })
        .finally(() => this.$emit('set-loader', false))
    },

    dismiss() {
      const payload = { subscription_periods: this.$refs.subscriptionsTable.getSelectedIds() }

      this.$emit('set-loader', true)
      apiTo
        .DISMISS_TASK(this.taskName, this.quickView.provider.id, payload)
        .then(this.taskActionTaken)
        .catch(() => this.$emit('set-loader', false))
    },

    send() {
      const formData = new FormData()
      const data = {
        subscription_periods: this.$refs.subscriptionsTable.getSelectedIds(),
        contacts: this.$refs.contactsTable.getSelectedIds(),
        email_template: this.quickView.email_template,
        attachments: this.attachments || []
      }

      createFormData(formData, data)

      this.$emit('set-loader', true)
      apiTo
        .PROCESS_TASK(this.taskName, this.quickView.provider.id, formData)
        .then(this.taskActionTaken)
        .catch(() => this.$emit('set-loader', false))
    },

    taskActionTaken() {
      const quickViewSubs = this.quickView.subscription_periods.length
      const selectedSubs = this.$refs.subscriptionsTable.getSelectedIds().length
      const allSubsAreSent = quickViewSubs === selectedSubs

      if (allSubsAreSent) this.checkQuickView()
      else this.reFetchQuickView()
      this.resetAttachments()
    },

    previewDefaultAttachment(attachmentProps) {
      let index = this.quickviewData.attachments.findIndex(
        file => file.name === attachmentProps.name && attachmentProps.system_generated
      )
      const attachment = this.quickviewData.attachments[index]
      apiTo.TASK_PREVIEW_ATTACHMENT(this.taskName, attachment.name, {
        subscription_periods: this.$refs.subscriptionsTable.getSelectedIds()
      })
    },

    resetAttachments() {
      this.$refs.emailAttachments.resetEmailAttachments()
    }
  }
}
</script>
